import React, {CSSProperties} from 'react';
import clsx from 'clsx';
import {Skeleton, type SkeletonProps} from '@mui/material';
import {
	composeClasses,
	styled,
	WithClasses,
	Theme,
	useTheme,
} from '@netx/core-components/Theme';
import type {AttributeProps} from '../Attribute';
import {
	attributeSkeletonClasses,
	getAttributeSkeletonUtilityClass,
	AttributeSkeletonClasses,
	ATTRIBUTE_SKELETON_CLASS_NAME,
} from './attributeSkeletonClasses';

type AttributeSkeletonOwnerState = WithClasses<
	AttributeSkeletonClasses,
	{
		readOnly?: boolean;
	}
>;

const useUtilityClasses = (ownerState: AttributeSkeletonOwnerState) => {
	const {classes, readOnly} = ownerState;
	const slots = {
		root: ['root', readOnly && 'readOnly'],
		readOnlyText: ['readOnlyText'],
		skeletonItem: ['skeletonItem'],
		preDecorator: ['preDecorator'],
		inputArea: ['inputArea'],
		postDecorator: ['postDecorator'],
		readOnlyTextTitle: ['readOnlyTextTitle'],
		readOnlyTextValue: ['readOnlyTextValue'],
	};
	return composeClasses(slots, getAttributeSkeletonUtilityClass, classes);
};

const getTextFieldHeight = ({components}: Theme) => {
	const textFieldSize = components?.MuiTextField?.defaultProps?.size;
	if (textFieldSize === 'medium') return 48;
	if (textFieldSize === 'small') return 40;
	return 56;
};

const AttributeSkeletonRoot = styled('div', {
	name: ATTRIBUTE_SKELETON_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {readOnly} = ownerState as AttributeSkeletonOwnerState;
	return {
		flex: '0 0 auto',
		display: 'flex',
		minWidth: 0,
		...(readOnly
			? {
					margin: theme.spacing(0.5, 1, 0.5, 1),
			  }
			: {
					margin: theme.spacing(2, 0, 1, 0),
			  }),
	};
});

const AttributeSkeletonItem = styled(Skeleton, {
	name: ATTRIBUTE_SKELETON_CLASS_NAME,
	slot: 'SkeletonItem',
})(({theme}) => ({
	flex: '0 0 auto',
	[`&.${attributeSkeletonClasses.inputArea}`]: {
		borderRadius: 4,
	},
	[`&.${attributeSkeletonClasses.preDecorator}`]: {
		borderRadius: '50%',
		marginRight: 8,
	},
	[`&.${attributeSkeletonClasses.postDecorator}`]: {
		borderRadius: '50%',
		marginLeft: 8,
		marginRight: -4,
	},
	[`&.${attributeSkeletonClasses.readOnlyTextTitle}`]: {
		marginBottom: theme.spacing(0.5),
	},
	// [`&.${attributeSkeletonClasses.readOnlyTextValue}`]: {

	// },
}));

const AttributeSkeletonReadOnlyText = styled('div', {
	name: ATTRIBUTE_SKELETON_CLASS_NAME,
	slot: 'ReadOnlyText',
})(({theme}) => ({
	width: '100%',
}));

export interface AttributeSkeletonProps
	extends WithClasses<
		AttributeSkeletonClasses,
		Partial<Omit<AttributeProps, 'classes' | 'onSearch'>>
	> {
	onSearch?: boolean;
	SkeletonProps?: Partial<SkeletonProps>;
	style?: CSSProperties;
}

export const AttributeSkeletonPrimitive = ({
	classes: _classes,
	className,

	clearable,
	onSearch,
	readOnly,
	SkeletonProps,
	attrData,
	enforceMandatory,
	hideLabel,
	value,
	limitHeightToContainer,
	loadingValue,
	placeholder,
	attributeWorking,
	alignActions,
	appendable,
	multipleValues,
	isMetadataLookup,
	isMetadataTarget,
	error,
	action,
	onLookup,
	forceClearable,
	disableFolksonomy,
	matchContainerHeight,
	changed,
	disabled,

	...props
}: AttributeSkeletonProps) => {
	const theme = useTheme();
	const textFieldHeight = getTextFieldHeight(theme);

	const actionSize = Math.max(textFieldHeight - 8, 40);
	const preDecorate = onSearch != null;
	const postDecorate = clearable === true;

	let inputWidthSubtraction = 0;
	if (preDecorate) inputWidthSubtraction += actionSize + 8;
	if (postDecorate) inputWidthSubtraction += actionSize + 8;

	const ownerState: AttributeSkeletonOwnerState = {
		classes: _classes,
		className,
		readOnly,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<AttributeSkeletonRoot
			className={clsx(classes.root, className)}
			{...props}
			ownerState={ownerState}
		>
			{(preDecorate && (
				<AttributeSkeletonItem
					variant="rectangular"
					className={clsx(classes.skeletonItem, classes.preDecorator)}
					height={actionSize}
					width={actionSize}
					{...SkeletonProps}
				/>
			)) ||
				null}
			{!readOnly ? (
				<AttributeSkeletonItem
					className={clsx(classes.skeletonItem, classes.inputArea)}
					height={textFieldHeight}
					width={`calc(100% - ${inputWidthSubtraction}px)`}
					variant="rectangular"
					{...SkeletonProps}
				/>
			) : (
				<AttributeSkeletonReadOnlyText className={classes.readOnlyText}>
					<AttributeSkeletonItem
						variant="text"
						className={clsx(classes.skeletonItem, classes.readOnlyTextTitle)}
						width={`calc(50% - ${inputWidthSubtraction}px)`}
						{...SkeletonProps}
					/>
					<AttributeSkeletonItem
						variant="text"
						className={clsx(classes.skeletonItem, classes.readOnlyTextValue)}
						width={`calc(100% - ${inputWidthSubtraction}px)`}
						{...SkeletonProps}
					/>
				</AttributeSkeletonReadOnlyText>
			)}
			{(postDecorate && (
				<AttributeSkeletonItem
					variant="rectangular"
					className={clsx(classes.skeletonItem, classes.postDecorator)}
					height={actionSize}
					width={actionSize}
					{...SkeletonProps}
				/>
			)) ||
				null}
		</AttributeSkeletonRoot>
	);
};

export const AttributeSkeleton = React.memo(AttributeSkeletonPrimitive);

export default AttributeSkeleton;
